import { useCallback, useEffect, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { ConfirmDialog } from '../ui/confirm-dialog.jsx';
import { EmptyState } from '../ui/empty-state.jsx';
import { ComposeNotificationModal } from '../notifications/compose-modal.jsx';
import { api, ApiError } from '../../api/index.js';
import { formatUtcInTimezone } from '../../lib/time.js';

const KIND_META = {
  announcement: { tone: 'border-accent/30 bg-accent-soft text-accent',  icon: 'fa-bullhorn',           label: 'Announcement' },
  alert:        { tone: 'border-warning/30 bg-warning-soft text-warning',icon: 'fa-triangle-exclamation', label: 'Alert' },
  order:        { tone: 'border-rose-200 bg-rose-soft text-rose-700',   icon: 'fa-clipboard-list',     label: 'Order' },
  system:       { tone: 'border-line bg-canvas text-mist',              icon: 'fa-gear',               label: 'System' },
};

const PRIORITY_TONE = {
  routine:   'text-mist',
  priority:  'text-accent',
  immediate: 'text-warning',
  flash:     'text-rose-700 animate-pulse',
};

export function NotificationsPage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const isAdmin = currentUser?.role === 'admin';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [retractState, setRetractState] = useState({ open: false, item: null });
  const [deleteState, setDeleteState] = useState({ open: false, item: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.notifications.list({ limit: 200 });
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleSend(payload) {
    await api.notifications.create(payload);
    toast.success('Notification dispatched.');
    setComposeOpen(false);
    await refresh();
  }

  async function handleRetract() {
    try {
      await api.notifications.retract(retractState.item.id);
      toast.success('Notification retracted.');
      setRetractState({ open: false, item: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Retract failed.');
    }
  }

  async function handleDelete() {
    try {
      await api.notifications.remove(deleteState.item.id);
      toast.success('Notification deleted.');
      setDeleteState({ open: false, item: null });
      await refresh();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Delete failed.');
    }
  }

  const items = data?.items || [];
  const stats = data?.stats || { total: 0, active: 0, retracted: 0, expired: 0 };

  return (
    <PageShell
      icon="fa-bell"
      title="Notifications"
      subtitle="Operator broadcasts and per-user alerts. Terminal-side delivery activates with Stage 2 mesh nodes."
      actions={
        isAdmin && (
          <button type="button" onClick={() => setComposeOpen(true)} className="btn-primary">
            <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            Send notification
          </button>
        )
      }
    >
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon="fa-bell"            label="Total"     value={stats.total}     tone="accent" />
        <StatCard icon="fa-circle-check"    label="Active"    value={stats.active}    tone="success" />
        <StatCard icon="fa-ban"             label="Retracted" value={stats.retracted} tone="rose" />
        <StatCard icon="fa-hourglass-end"   label="Expired"   value={stats.expired}   tone="warning" />
      </div>

      {error && (
        <div role="alert" className="card mb-4 border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="card px-6 py-16 text-center text-mist">
          <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
          <div className="text-sm">Loading notifications…</div>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon="fa-bell"
          title="No notifications yet"
          description={isAdmin ? 'Send the first notification to operators.' : 'Nothing has been sent yet.'}
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
                  <th className="px-5 py-3">Notification</th>
                  <th className="px-5 py-3">Kind</th>
                  <th className="px-5 py-3">Target</th>
                  <th className="px-5 py-3">Priority</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Sent</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => {
                  const km = KIND_META[n.kind] || KIND_META.announcement;
                  const isExpired = n.expiresAt && new Date(n.expiresAt) <= new Date();
                  const isRetracted = !!n.retractedAt;
                  const status = isRetracted ? 'retracted' : isExpired ? 'expired' : 'active';
                  return (
                    <tr key={n.id} className="border-b border-line last:border-b-0 hover:bg-canvas/40">
                      <td className="px-5 py-3 align-middle">
                        <div className="flex items-start gap-3">
                          <div className={'mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl border ' + km.tone}>
                            <i className={`fa-solid ${km.icon}`} aria-hidden="true" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-ink">{n.title}</div>
                            {n.body && (
                              <div className="mt-0.5 line-clamp-2 text-[12px] text-mist">{n.body}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${km.tone}`}>
                          {km.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <TargetCell type={n.targetType} value={n.targetValue} />
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <span className={'text-[11px] font-semibold uppercase tracking-wider ' + (PRIORITY_TONE[n.priority] || 'text-mist')}>
                          {n.priority}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-5 py-3 align-middle text-xs">
                        <div className="text-ink">{formatUtcInTimezone(n.createdAt, tz)}</div>
                        <div className="text-[11px] text-mist">by {n.sentByLogin || 'system'}</div>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setRetractState({ open: true, item: n })}
                            disabled={!isAdmin || isRetracted || isExpired}
                            title="Retract"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-warning-soft hover:text-warning disabled:opacity-30"
                          >
                            <i className="fa-solid fa-ban" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteState({ open: true, item: n })}
                            disabled={!isAdmin}
                            title="Delete"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30"
                          >
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ComposeNotificationModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={handleSend}
      />

      <ConfirmDialog
        open={retractState.open}
        title="Retract notification?"
        message="The notification will no longer appear in operator inboxes. Already-read copies are unchanged."
        confirmLabel="Retract"
        destructive
        onClose={() => setRetractState({ open: false, item: null })}
        onConfirm={handleRetract}
      />

      <ConfirmDialog
        open={deleteState.open}
        title="Delete notification?"
        message="The notification record is removed permanently along with its delivery state. Audit-log entries remain."
        confirmLabel="Delete"
        destructive
        onClose={() => setDeleteState({ open: false, item: null })}
        onConfirm={handleDelete}
      />
    </PageShell>
  );
}

function TargetCell({ type, value }) {
  const labels = {
    all:     { label: 'All operators', icon: 'fa-users' },
    role:    { label: `Role: ${value}`, icon: 'fa-user-shield' },
    user:    { label: `User #${value}`, icon: 'fa-user' },
    unit:    { label: `Unit #${value}`, icon: 'fa-sitemap' },
    channel: { label: `Channel #${value}`, icon: 'fa-comments' },
    person:  { label: `Person #${value}`, icon: 'fa-user' },
  };
  const m = labels[type] || { label: type, icon: 'fa-circle-dot' };
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-ink">
      <i className={`fa-solid ${m.icon} text-mist`} aria-hidden="true" />
      {m.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const meta = {
    active:    { tone: 'border-success/30 bg-success-soft text-success', icon: 'fa-circle-check',   label: 'Active' },
    retracted: { tone: 'border-rose-200 bg-rose-soft text-rose-700',     icon: 'fa-ban',            label: 'Retracted' },
    expired:   { tone: 'border-line bg-canvas text-mist',                icon: 'fa-hourglass-end',  label: 'Expired' },
  }[status] || { tone: 'border-line bg-canvas text-mist', icon: 'fa-circle', label: status };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.tone}`}>
      <i className={`fa-solid ${meta.icon} text-[9px]`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function StatCard({ icon, label, value, tone = 'accent' }) {
  const tones = {
    accent:  'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    rose:    'bg-rose-soft text-rose-700',
  };
  return (
    <div className="card flex items-center justify-between px-5 py-4">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">{value}</div>
      </div>
      <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone]}`}>
        <i className={`fa-solid ${icon} text-lg`} aria-hidden="true" />
      </div>
    </div>
  );
}
